import { z } from 'zod';

// Request schemas
export const junkInSchema = z.object({
  tokenMint: z.string().min(1, 'Token mint is required'),
  amount: z.string().regex(/^\d+$/, 'Amount must be a valid lamports string'),
});

export const historyQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().min(1).max(100).default(20),
});

export const cronAuthSchema = z.object({
  authorization: z
    .string()
    .refine((auth) => auth.startsWith('Bearer ') && auth.slice(7).length > 0, 'Invalid authorization header'),
});

export const chestOpenSchema = z.object({
  sol_amount: z.string().regex(/^\d+$/, 'SOL amount must be valid lamports'),
});

export const solWithdrawSchema = z.object({
  sol_amount: z.string().regex(/^\d+$/, 'SOL amount must be valid lamports'),
});

// Response schemas
export const userProgressSchema = z.object({
  burns_since_last_spin: z.number().min(0).max(5),
  can_jackpot: z.boolean(),
  sol_balance: z.string(), // SOL credits available for chest opening
  remainder_sol: z.string(), // Leftover SOL from chest openings
  total_contributed_sol: z.string(), // Lifetime pool contributions
});

export const poolStateSchema = z.object({
  total_prize_lamports: z.string(),
  service_fee_collected_lamports: z.string(),
  total_chests_opened: z.string(),
  pool_size_usd: z.number(),
  available_tiers: z.array(z.number()),
  next_unlock_threshold: z.number().nullable(),
  expected_return_rate: z.number(),
});

export const outcomeSchema = z.object({
  result: z.enum([
    'meme_reward',
    'jackpot_progress',
    'chest_reward',
    'no_reward',
    'sol_withdrawal',
    'token_burned',
    'nft_burned',
    'rent_reclaimed',
  ]),
  is_jackpot: z.boolean(),
  tier: z.number().optional(),
  tier_name: z.string().optional(),
  tier_color: z.string().optional(),
  payout_lamports: z.string().optional(),
  usd_value: z.number().optional(),
  sol_awarded: z.string().optional(),
});

export const rewardSchema = z.object({
  type: z.enum(['meme', 'jackpot', 'chest_reward']),
  amount_lamports: z.string(),
  multiplier: z.number().optional(),
  tier: z.number().optional(),
  tier_name: z.string().optional(),
  tier_color: z.string().optional(),
  usd_value: z.number().optional(),
  is_jackpot_win: z.boolean().default(false),
});

export const junkOutcomeSchema = z.object({
  success: z.boolean(),
  user_progress: userProgressSchema,
  rewards: z.array(rewardSchema).default([]),
  pools: poolStateSchema,
});

export const chestOpenOutcomeSchema = z.object({
  success: z.boolean(),
  user_progress: userProgressSchema,
  rewards: z.array(rewardSchema).default([]),
  pools: poolStateSchema,
  chests_opened: z.number(),
  sol_used: z.string(),
  pool_contribution: z.string(),
  service_fee: z.string(),
  new_remainder: z.string(),
  available_tiers: z.array(z.number()),
});

export const solWithdrawOutcomeSchema = z.object({
  success: z.boolean(),
  user_progress: userProgressSchema,
  amount_withdrawn_lamports: z.string(),
  sol_balance_spent: z.string(),
});

export const historyItemSchema = z.object({
  id: z.number(),
  order_id: z.string(),
  kind: z.enum([
    'deposit',
    'withdraw',
    'burn_token',
    'burn_nft',
    'chest_open',
    'chest_reward',
  ]),
  sol_delta_lamports: z.string(), // + for credits, - for debits
  tx_hash: z.string().nullable(),
  created_at: z.string(), // ISO timestamp
  metadata: z.record(z.any()).optional(),
});

export const historyPageSchema = z.object({
  items: z.array(historyItemSchema),
  next_cursor: z.number().nullable(),
  has_more: z.boolean(),
});

export const userConnectResponseSchema = z.object({
  success: z.boolean(),
  wallet_address: z.string(),
  user_progress: userProgressSchema,
  message: z.string(),
});

export const userDisconnectResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

export const apiErrorSchema = z.object({
  error: z.string(),
  code: z.string().optional(),
  details: z.record(z.any()).optional(),
});

// Type exports
export type JunkInRequest = z.infer<typeof junkInSchema>;
export type HistoryQuery = z.infer<typeof historyQuerySchema>;
export type ChestOpenRequest = z.infer<typeof chestOpenSchema>;
export type SolWithdrawRequest = z.infer<typeof solWithdrawSchema>;
export type UserProgress = z.infer<typeof userProgressSchema>;
export type PoolState = z.infer<typeof poolStateSchema>;
export type Outcome = z.infer<typeof outcomeSchema>;
export type Reward = z.infer<typeof rewardSchema>;
export type JunkOutcome = z.infer<typeof junkOutcomeSchema>;
export type ChestOpenOutcome = z.infer<typeof chestOpenOutcomeSchema>;
export type SolWithdrawOutcome = z.infer<typeof solWithdrawOutcomeSchema>;
export type HistoryItem = z.infer<typeof historyItemSchema>;
export type HistoryPage = z.infer<typeof historyPageSchema>;
export type UserConnectResponse = z.infer<typeof userConnectResponseSchema>;
export type UserDisconnectResponse = z.infer<typeof userDisconnectResponseSchema>;
export type ApiError = z.infer<typeof apiErrorSchema>;

// Token and NFT interfaces
export interface TokenInfo {
  mint: string;
  symbol: string;
  name: string;
  decimals: number;
  logoUrl?: string;
  isValueless: boolean;
  currentValueLamports: bigint;
}

export interface NFTInfo {
  mint: string;
  name: string;
  symbol: string;
  image?: string;
  collection?: string;
}

export interface SelectedItem {
  type: 'token' | 'nft';
  item: TokenInfo | NFTInfo;
}

// Referral types
export interface ReferralStats {
  user_code: string;
  total_referrals: number;
  tier: string;
  pending_rewards: string;
  lifetime_rewards: string;
}

// Points Lottery schemas
export const lotteryTierSchema = z.object({
  id: z.number(),
  thresholdPoints: z.string(),
  prizeLabel: z.string(),
  prizeAmountLamports: z.string(),
  quantity: z.number(),
  unlockOrder: z.number(),
  isUnlocked: z.boolean(),
});

export const lotterySeasonSchema = z.object({
  id: z.number(),
  seasonNo: z.number(),
  phase: z.enum(['warmup', 'active', 'settling', 'complete']),
  warmupEndsAt: z.string().nullable().optional(),
  activeEndsAt: z.string().nullable().optional(),
  totalPointsCommitted: z.string(),
  uniqueUsers: z.string(),
  maxPossibleWinLamports: z.string(),
});

export const lotteryUserSummarySchema = z.object({
  availablePoints: z.string(),
  lockedPoints: z.string(),
  contributedPoints: z.string(),
});

export const lotterySeasonResponseSchema = z.object({
  season: lotterySeasonSchema,
  tiers: z.array(lotteryTierSchema),
  user: lotteryUserSummarySchema.nullable(),
});

export const lotteryLeaderboardEntrySchema = z.object({
  rank: z.number(),
  userId: z.number(),
  walletAddress: z.string(),
  points: z.string(),
  isCurrentUser: z.boolean(),
});

export const lotteryLeaderboardResponseSchema = z.object({
  items: z.array(lotteryLeaderboardEntrySchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  user: z.object({
    rank: z.number().nullable(),
    points: z.string(),
  }),
});

// Points Leaderboard schemas (separate from lottery leaderboard)
export const rankingTypeSchema = z.enum(['burned', 'cumulative', 'unused']);
export type RankingType = z.infer<typeof rankingTypeSchema>;

export const pointsLeaderboardEntrySchema = z.object({
  rank: z.number(),
  userId: z.number(),
  address: z.string(),
  points: z.string(),
  isCurrentUser: z.boolean(),
});

export const pointsLeaderboardResponseSchema = z.object({
  items: z.array(pointsLeaderboardEntrySchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  range: z.enum(['all', '30d', '7d', '24h']),
  type: rankingTypeSchema.optional(), // Optional for backward compatibility with old backend
  user: z.object({
    rank: z.number().nullable(),
    points: z.string(),
  }).nullable(),
});

export const lotteryWinnerRowSchema = z.object({
  id: z.number(),
  prizeLabel: z.string(),
  prizeAmountLamports: z.string(),
  userId: z.number().nullable().optional(),
  claimedAt: z.string().nullable().optional(),
  isClaimed: z.boolean(),
  isVerified: z.boolean().optional(),
  canClaim: z.boolean().optional(),
  walletAddressMasked: z.string().nullable().optional(),
  isCurrentUser: z.boolean(),
});

export const lotteryHistoryResponseSchema = z.object({
  season: z.object({
    id: z.number(),
    seasonNo: z.number(),
    phase: z.enum(['warmup', 'active', 'settling', 'complete']),
    warmupEndsAt: z.string().nullable().optional(),
    activeEndsAt: z.string().nullable().optional(),
  }),
  winners: z.array(lotteryWinnerRowSchema),
});

export type LotteryTier = z.infer<typeof lotteryTierSchema>;
export type LotterySeasonInfo = z.infer<typeof lotterySeasonSchema>;
export type LotteryUserSummary = z.infer<typeof lotteryUserSummarySchema>;
export type LotterySeasonResponse = z.infer<typeof lotterySeasonResponseSchema>;
export type LotteryLeaderboardEntry = z.infer<typeof lotteryLeaderboardEntrySchema>;
export type LotteryLeaderboardResponse = z.infer<typeof lotteryLeaderboardResponseSchema>;
export type PointsLeaderboardEntry = z.infer<typeof pointsLeaderboardEntrySchema>;
export type PointsLeaderboardResponse = z.infer<typeof pointsLeaderboardResponseSchema>;
export type LotteryWinnerRow = z.infer<typeof lotteryWinnerRowSchema>;
export type LotteryHistoryResponse = z.infer<typeof lotteryHistoryResponseSchema>;

// NFT Tier enum
export const nftTierSchema = z.enum(['diamond', 'black', 'gold', 'silver', 'bronze']);
export type NFTTier = z.infer<typeof nftTierSchema>;

// Season Results schemas
export const seasonResultsWinnerSchema = z.object({
  id: z.number(),
  userId: z.number().nullable(),
  maskedAddress: z.string(),
  prizeLabel: z.string(),
  prizeAmountLamports: z.string(),
  drawOrder: z.number(),
  claimed: z.boolean(),
  isVerified: z.boolean(),
  canClaim: z.boolean(),
});

export const seasonResultsUserResultSchema = z.object({
  rank: z.number().nullable(),
  frozenRank: z.number().nullable(), // Frozen rank from lottery_contribution_rank table
  pointsContributed: z.string(),
  isWinner: z.boolean(),
  prize: z.object({
    winnerId: z.number(),
    label: z.string(),
    amountLamports: z.string(),
    claimed: z.boolean(),
    claimedAt: z.string().nullable(),
    isVerified: z.boolean(),
    canClaim: z.boolean(),
  }).nullable(),
  nftEligible: z.boolean(),
  nftMinted: z.boolean(),
  nftTier: nftTierSchema.nullable(),
  nftRank: z.number().nullable(),
});

export const seasonResultsResponseSchema = z.object({
  season: z.object({
    id: z.number(),
    seasonNo: z.number(),
    phase: z.enum(['warmup', 'active', 'settling', 'complete']),
    totalPoints: z.string(),
    totalParticipants: z.number(),
    poolLamports: z.string(),
  }),
  userResult: seasonResultsUserResultSchema.nullable(),
  winners: z.array(seasonResultsWinnerSchema),
  tierStats: z.object({
    diamond: z.number(),
    black: z.number(),
    gold: z.number(),
    silver: z.number(),
    bronze: z.number(),
  }),
});

// NFT Mint Response schema
export const nftMintResponseSchema = z.object({
  seasonNo: z.number(),
  tier: nftTierSchema,
  rank: z.number().nullable(),
  pointsContributed: z.string(),
  // For real minting flow
  orderId: z.string().optional(),
  serializedTx: z.string().optional(),
  // For legacy/confirmed mints
  mintAddress: z.string().optional(),
  mintedAt: z.string().optional(),
});

// Unclaimed Rewards schema
export const unclaimedRewardItemSchema = z.object({
  seasonId: z.number(),
  seasonNo: z.number(),
  unclaimedReward: z.object({
    winnerId: z.number(),
    prizeLabel: z.string(),
    prizeAmountLamports: z.string(),
    isVerified: z.boolean().optional(),
    canClaim: z.boolean().optional(),
  }).nullable(),
  nftNotMinted: z.boolean(),
  pointsContributed: z.string(),
});

export const unclaimedRewardsResponseSchema = z.array(unclaimedRewardItemSchema);

// User NFTs Response schema
export const userNFTSchema = z.object({
  id: z.number(),
  seasonNo: z.number(),
  tier: nftTierSchema,
  rank: z.number().nullable(),
  pointsContributed: z.string(),
  mintAddress: z.string().nullable(),
  mintedAt: z.string().nullable(),
  collectionAddress: z.string().nullable(),
  imageUrl: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
});

export const userNFTsResponseSchema = z.array(userNFTSchema);

// Share Result Response schema
export const shareResultResponseSchema = z.object({
  seasonNo: z.number(),
  roi: z.number(),
  rewardSOL: z.number(),
  rewardUSD: z.number(),
  pointsInvested: z.string(),
  referralCode: z.string(),
  shareImageUrl: z.string().nullable(),
});

// Type exports for new schemas
export type SeasonResultsWinner = z.infer<typeof seasonResultsWinnerSchema>;
export type SeasonResultsUserResult = z.infer<typeof seasonResultsUserResultSchema>;
export type SeasonResultsResponse = z.infer<typeof seasonResultsResponseSchema>;
export type NFTMintResponse = z.infer<typeof nftMintResponseSchema>;
export type UnclaimedRewardItem = z.infer<typeof unclaimedRewardItemSchema>;
export type UnclaimedRewardsResponse = z.infer<typeof unclaimedRewardsResponseSchema>;
export type UserNFT = z.infer<typeof userNFTSchema>;
export type UserNFTsResponse = z.infer<typeof userNFTsResponseSchema>;
export type ShareResultResponse = z.infer<typeof shareResultResponseSchema>;

export const pointsLedgerEntrySchema = z.object({
  id: z.number(),
  type: z.enum([
    'chest_open',
    'chest_invitee_bonus',
    'chest_inviter_bonus', // Referral bonus when invitee opens chests
    'burn_bonus', // Legacy - kept for backward compatibility
    'burn_token_bonus', // Points from burning tokens
    'burn_token_inviter_bonus', // Referral bonus when invitee burns tokens
    'burn_nft_bonus', // Points from burning NFTs
    'burn_nft_inviter_bonus', // Referral bonus when invitee burns NFTs
    'burn_mixed_bonus', // Points from burning both tokens and NFTs together
    'burn_mixed_inviter_bonus', // Referral bonus when invitee burns mixed
    'dust_swap_bonus', // Points from dust swap
    'dust_swap_inviter_bonus', // Referral bonus when invitee does dust swap
    'referral_bonus',
    'manual_adjustment',
    'contribution',
    'payout',
    'reversal',
    'social_task',
    'share_bonus',
    'backpack_campaign_bonus',
    'solflare_campaign_bonus',
    'magic_eden_campaign_bonus',
    'referral_campaign_bonus',
    'gate_campaign_bonus',
    'gate_wallet_campaign_bonus', // Alternative name for Gate Wallet bonus
    'breakpoint_winner_campaign_bonus',
  ]),
  pointsDelta: z.string(),
  balanceAfter: z.string(),
  lifetimeAfter: z.string(),
  seasonId: z.number().nullable(),
  reference: z.string().nullable(),
  metadata: z.string().nullable(),
  createdAt: z.string().nullable(),
});

export const pointsBalanceResponseSchema = z.object({
  userId: z.number(),
  walletAddress: z.string(),
  balance: z.object({
    availablePoints: z.string(),
    lockedPoints: z.string(),
    lifetimePoints: z.string(),
    contributedPoints: z.string(),
  }),
  season: z
    .object({
      id: z.number(),
      seasonNo: z.number(),
      phase: z.enum(['warmup', 'active', 'settling', 'complete']),
    })
    .nullable(),
  ledger: z.object({
    total: z.number(),
    hasMore: z.boolean(),
    items: z.array(pointsLedgerEntrySchema),
  }),
});

export const pointsLedgerResponseSchema = z.object({
  userId: z.number(),
  walletAddress: z.string(),
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  hasMore: z.boolean(),
  items: z.array(pointsLedgerEntrySchema),
});

export type PointsLedgerEntry = z.infer<typeof pointsLedgerEntrySchema>;
export type PointsBalanceResponse = z.infer<typeof pointsBalanceResponseSchema>;
export type PointsLedgerResponse = z.infer<typeof pointsLedgerResponseSchema>;
